import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import contractSpecificAgreementsActions from './contractSpecificAgreements/actions';
import Api from '../../../../helpers/api';
import { fieldValidations, resolvedErrorMessage, sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { scrollToFirstError } from '../../../../helpers/scrollToFirstError';
import { isFinanceOrAdminOrSystemAdminRole } from '../../../../helpers/userRoles';
import { t } from '../../../../i18n';
import { translationsRootKey } from '../../../../settings';

const api = new Api('companies/:id');
const clientsListApi = new Api('companies');
const administratorsListApi = new Api('administrators/list');
const divisionsApi = new Api('divisions/list');
const unitsApi = new Api('units/list');

const noDivisionsKey = 'noDivisions';

function* loadData({ payload }) {
  try {
    const { id, isEdit, role } = payload;
    const result = yield call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR, null, true);
    yield put(contractSpecificAgreementsActions.loadRequest({ company_id: id }));

    if (result) {
      const { data } = result;
      yield put(
        actions.loadDataSuccess({
          data,
        }),
      );

      if (isEdit) {
        yield put(actions.edit({ id, role, company: { ...data, address: '' } }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateClient(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const company = keyByProperty(propsArray, 'name', prop => prop.value);

    if (!company.breaks_enabled) {
      company.breaks_duration = 0;
      company.breaks_compensation = false;
    }

    const { account_manager, activation_rep, sales_rep, state, ...otherProps } = company;

    const child_ids = [];

    if (company.divisions[0] && company.divisions[0].value === noDivisionsKey) {
      company.direct_units.forEach(unit => child_ids.push(unit.value));
    } else company.divisions.forEach(division => child_ids.push(division.value));

    if (!child_ids.length) {
      yield put(
        actions.saveError({
          data: {
            base: [t(`${translationsRootKey}.companies.company.unit_missed_error`)],
          },
        }),
      );
    }

    const result = child_ids.length
      ? yield call(
          sendRequest,
          api.updateItem,
          {
            data: {
              company: {
                ...otherProps,
                account_manager_id: account_manager,
                activation_rep_id: activation_rep,
                sales_rep_id: sales_rep,
                child_ids,
                country_id: company.country,
                state_id: state,
              },
            },
            id,
          },
          notifications.REQUEST_ERROR,
        )
      : null;

    if (result) {
      if (result?.status === 200) {
        yield put(actions.saveSuccess());

        yield put(push(`/companies/${id}`));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', {
              item: t(`${translationsRootKey}.companies.company.company_title`),
            }),
          }),
        );
      } else {
        const validations = fieldValidations(result) ?? {};
        const base = !child_ids.length ? [t(`${translationsRootKey}.companies.company.unit_missed_error`)] : null;

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (base || Object.keys(validations).length) {
          yield put(actions.saveError({ data: { ...validations, base } }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteClient(data) {
  try {
    const { payload } = data;
    const { id } = payload;

    const result = yield call(sendRequest, api.deleteItem, { id }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(actions.deleteSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deleted_successfully', {
            item: t(`${translationsRootKey}.companies.company.company_title`),
          }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addClient(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;

    const propsArray = Object.values(editForm);
    const company = keyByProperty(propsArray, 'name', prop => prop.value);

    if (!company.breaks_enabled) {
      company.breaks_duration = 0;
      company.breaks_compensation = false;
    }

    const { account_manager, activation_rep, sales_rep, state, ...otherProps } = company;

    const child_ids = [];

    if (company.divisions[0] && company.divisions[0].value === noDivisionsKey) {
      company.direct_units.forEach(unit => child_ids.push(unit.value));
    } else company.divisions.forEach(division => child_ids.push(division.value));

    if (!child_ids.length) {
      yield put(
        actions.saveError({
          data: {
            base: [t(`${translationsRootKey}.companies.company.unit_missed_error`)],
          },
        }),
      );
    }

    const result = child_ids.length
      ? yield call(
          sendRequest,
          clientsListApi.createItem,
          {
            data: {
              company: {
                ...otherProps,
                account_manager_id: account_manager,
                activation_rep_id: activation_rep,
                sales_rep_id: sales_rep,
                child_ids,
                country_id: company.country,
                state_id: state,
              },
            },
          },
          notifications.REQUEST_ERROR,
        )
      : null;

    if (result) {
      if (result.data) {
        const { id } = result.data;
        yield put(actions.saveSuccess());

        yield put(push(`/companies/${id}`));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t(`${translationsRootKey}.companies.company.company_title`),
            }),
          }),
        );
      } else {
        const validations = fieldValidations(result);

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (validations) {
          yield put(actions.saveError({ data: validations }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    const result = yield call(sendRequest, clientsListApi.loadFormData, {}, notifications.REQUEST_ERROR);

    if (result) {
      yield put(actions.loadFormDataSuccess(result));

      if (payload.id === 'add') {
        yield put(actions.create({ role: payload.role }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadManagersFormData({ payload }) {
  try {
    const isAcceptedRole = isFinanceOrAdminOrSystemAdminRole(payload.role);
    const result = isAcceptedRole
      ? yield call(sendRequest, administratorsListApi.load, { search: { pageSize: 2000 } }, notifications.REQUEST_ERROR)
      : null;

    if (result) {
      const { data } = result.data;

      yield put(actions.loadManagersFormData({ data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* filterByLegalEntity(data) {
  const isLegalEntityEnabled = yield select(state =>
    Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
  );

  if (!isLegalEntityEnabled) return data;

  const legalEntityId = yield select(
    state => state.Company.company?.legal_entity?.id || state.Company.editForm?.legal_entity_id?.value,
  );

  return legalEntityId ? data.filter(item => item.legal_entity_id === legalEntityId) : data;
}

function* filterDivisions({ payload }) {
  try {
    const { filter, isNoDivisionsSelected } = payload;
    const result =
      filter && !isNoDivisionsSelected
        ? yield call(
            sendRequest,
            divisionsApi.load,
            { search: { filter, pageSize: 100, column: 'name' } },
            notifications.REQUEST_ERROR,
          )
        : null;

    if (result) {
      const { data } = result.data;
      const filtered = yield call(filterByLegalEntity, data);

      yield put(actions.filterDivisionsSuccess({ data: filtered }));
    } else if (!filter) {
      yield put(actions.filterDivisionsSuccess({ data: [] }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* filterUnits({ payload }) {
  try {
    const { filter } = payload;
    const result = filter
      ? yield call(
          sendRequest,
          unitsApi.load,
          { search: { filter, pageSize: 100, column: 'name' } },
          notifications.REQUEST_ERROR,
        )
      : null;

    if (result) {
      const { data } = result.data;
      const filtered = yield call(filterByLegalEntity, data);

      yield put(actions.filterUnitsSuccess({ data: filtered }));
    } else if (!filter) {
      yield put(actions.filterUnitsSuccess({ data: [] }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export { addClient };

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updateClient),
    takeEvery(actions.DELETE, deleteClient),
    takeEvery(actions.ADD, addClient),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.LOAD_FORM_DATA, loadFormData),
    takeEvery(actions.EDIT, loadManagersFormData),
    takeEvery(actions.CREATE, loadManagersFormData),
    takeEvery(actions.FILTER_DIVISIONS, filterDivisions),
    takeEvery(actions.FILTER_UNITS, filterUnits),
    takeEvery(actions.SAVE_ERROR, scrollToFirstError),
  ]);
}
